import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, type FormikProps } from 'formik';
import { Text, TextArea } from '@rsos/base-components';
import { customGenericStyles } from '@rsos/base-components/Select/Select.styles';
import { ErrorWrapper } from '@rsos/base-components/form/ErrorsWrapper';
import { AgencyStates } from '@rsos/capstone/src/constants/Systems';
import { Button, Select } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import type { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { TOW_PRO } from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import {
  closeResourcePanel,
  markTransportSeen,
  setIsEditing,
  setResourcePanelView,
  setSelectedRequest,
  useBreadCrumbs,
  useCreateTowProRequestMutation,
  useGetTowProMunicipalitiesQuery,
  useUpdateTowProRequestMutation,
  type TowProRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { Label } from '../../../../AirAmbulanceForm/AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import SelectLocationBanner from '../../../../AirAmbulanceForm/AirAmbulanceForm/SelectLocationBanner/SelectLocationBanner';
import TowProAddressField from './TowProAddressField';
import styles from './TowProForm.module.css';
import TowProFormSection from './TowProFormSection';
import { findCallByPhoneNumber, getCallLocation } from './towProCallLookup';
import {
  TOWPRO_REASON_OPTIONS,
  type TowProFormValues,
  type TowProSelectOption,
} from './towProFormConstants';
import {
  buildTowProDispatchRequest,
  formValuesFromRequest,
  getVinWarning,
  normalizeVin,
  validateTowProForm,
} from './towProFormUtils';
import { useTowProInitialFormValues } from './useTowProInitialFormValues';
import { useTowProLocationField } from './useTowProLocationField';
import { useTowProVinAutofill } from './useTowProVinAutofill';

const STATE_OPTIONS: TowProSelectOption[] = AgencyStates.map(
  ({ label }: { label: string }) => ({ value: label, label }),
);

const SUBMIT_ERROR_MESSAGE =
  'Unable to submit the tow request. Please try again.';

/** Form fields backed by a string value, i.e. everything but coordinates. */
type TowProStringField = {
  [K in keyof TowProFormValues]: TowProFormValues[K] extends string ? K : never;
}[keyof TowProFormValues];

interface TextFieldProps {
  formik: FormikProps<TowProFormValues>;
  name: TowProStringField;
  label: string;
  placeholder?: string;
  onValueChange?: (value: string) => void;
  /** Normalizes the raw input before it is stored in the form. */
  transformValue?: (value: string) => string;
  /** Advisory message shown below the input; unlike an error it never blocks submission. */
  warning?: string;
}

function TextField({
  formik,
  name,
  label,
  placeholder,
  onValueChange,
  transformValue,
  warning,
}: TextFieldProps): JSX.Element {
  const error =
    formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  return (
    <div className={styles.field}>
      <Label>
        <Text size="small" color="primaryText">
          {label}
        </Text>
      </Label>
      <InputText
        id={name}
        name={name}
        type="text"
        placeholder={placeholder}
        setFieldValue={formik.setFieldValue}
        value={formik.values[name]}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (transformValue) {
            const value = transformValue(e.target.value);
            formik.setFieldValue(name, value);
            onValueChange?.(value);
          } else {
            formik.handleChange(e);
            onValueChange?.(e.target.value);
          }
        }}
        onBlur={formik.handleBlur}
        error={error}
        style={{ fontSize: '14px' }}
      />
      {warning && !error && <div className={styles.warningText}>{warning}</div>}
    </div>
  );
}

interface SelectFieldProps {
  formik: FormikProps<TowProFormValues>;
  name: TowProStringField;
  label: string;
  options: TowProSelectOption[];
  placeholder: string;
  isLoading?: boolean;
}

interface SelectControlState {
  selectProps: {
    theme: {
      colors: {
        lightDanger: string;
        heavyLineOutline: string;
      };
    };
  };
  isDisabled: boolean;
}

function SelectField({
  formik,
  name,
  label,
  options,
  placeholder,
  isLoading = false,
}: SelectFieldProps): JSX.Element {
  const selectedOption =
    options.find(option => option.value === formik.values[name]) ?? null;
  const error =
    formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : undefined;

  const selectStyles = useMemo(() => {
    const baseStyles = customGenericStyles as Record<string, unknown>;
    return {
      ...baseStyles,
      control: (
        base: Record<string, unknown>,
        state: SelectControlState,
      ): Record<string, unknown> => ({
        ...((
          baseStyles.control as (
            base: Record<string, unknown>,
            state: SelectControlState,
          ) => Record<string, unknown>
        )?.(base, state) || {}),
        borderColor: error
          ? state.selectProps.theme.colors.lightDanger
          : state.selectProps.theme.colors.heavyLineOutline,
        opacity: state.isDisabled ? 0.5 : 1,
        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
        pointerEvents: 'auto',
      }),
    };
  }, [error]);

  return (
    <div className={styles.field}>
      <Label>
        <Text size="small" color="primaryText">
          {label}
        </Text>
      </Label>
      <Select
        name={name}
        classNamePrefix={name}
        options={options}
        placeholder={placeholder}
        isClearable={false}
        isSearchable={false}
        isLoading={isLoading}
        onChange={(option: TowProSelectOption | null) => {
          // Validate the new value, then mark touched without validating so
          // the stale empty value never renders a transient Required error.
          formik.setFieldValue(name, option?.value ?? '', true);
          formik.setFieldTouched(name, true, false);
        }}
        onBlur={() => formik.setFieldTouched(name, true)}
        value={selectedOption}
        styles={selectStyles}
      />
      {error && <ErrorWrapper error={error} />}
    </div>
  );
}

interface TowProFormProps {
  /** When set the form edits this request and resubmits it to TowPro. */
  existingRequest?: TowProRequestResponse | null;
}

/**
 * Tow request form for creating a request or editing an active one. Section
 * layout mirrors the TowPro design: Request (location, region, reason,
 * related call), Vehicle, Incident & Officer, Vehicle Owner, and Additional
 * notes.
 */
function TowProForm({ existingRequest = null }: TowProFormProps): JSX.Element {
  const dispatch = useDispatch();
  const breadCrumbs = useBreadCrumbs();
  const formRef = useRef<FormikProps<TowProFormValues>>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [createTowProRequest, { isLoading: isCreating }] =
    useCreateTowProRequestMutation();
  const [updateTowProRequest, { isLoading: isUpdating }] =
    useUpdateTowProRequestMutation();
  const isSubmitting = isCreating || isUpdating;
  const { data: municipalities, isFetching: isLoadingRegions } =
    useGetTowProMunicipalitiesQuery();

  const regionOptions = useMemo((): TowProSelectOption[] => {
    if (!municipalities) {
      return [];
    }
    return municipalities.subMunicipalities.flatMap(subMunicipality =>
      subMunicipality.regions.map(region => ({
        value: String(region.id),
        label: region.name,
      })),
    );
  }, [municipalities]);

  const handleLocationResolved = useCallback(
    (location: {
      address?: string;
      latitude?: number;
      longitude?: number;
    }): void => {
      const formik = formRef.current;
      if (!formik) {
        return;
      }
      if ('address' in location) {
        formik.setFieldValue('address', location.address ?? '');
      }
      if ('latitude' in location) {
        formik.setFieldValue('latitude', location.latitude ?? null);
      }
      if ('longitude' in location) {
        formik.setFieldValue('longitude', location.longitude ?? null);
      }
    },
    [],
  );

  const {
    geocodedResults,
    isReverseGeocoding,
    handleAddressChange,
    handleSelectSuggestion,
    triggerReverseGeocode,
  } = useTowProLocationField({ onLocationResolved: handleLocationResolved });

  const callSeededValues = useTowProInitialFormValues();
  const initialValues = useMemo(
    () =>
      existingRequest
        ? formValuesFromRequest(existingRequest)
        : callSeededValues,
    [existingRequest, callSeededValues],
  );
  const callQueue = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.callQueue,
  );
  const handleVinChange = useTowProVinAutofill(formRef);

  // A call location can arrive as bare coordinates; resolve its address once
  // on open.
  useEffect(() => {
    if (
      !initialValues.address &&
      initialValues.latitude != null &&
      initialValues.longitude != null
    ) {
      triggerReverseGeocode({
        latitude: initialValues.latitude,
        longitude: initialValues.longitude,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount
  }, []);

  // Typing a phone number that matches a call in the queue pulls that call's
  // location into the form, unless a location was already chosen.
  const handleRelatedCallChange = useCallback(
    (value: string): void => {
      const formik = formRef.current;
      if (!formik) {
        return;
      }
      if (formik.values.latitude != null && formik.values.longitude != null) {
        return;
      }

      const call = findCallByPhoneNumber(callQueue ?? {}, value);
      const callLocation = getCallLocation(call);
      if (!callLocation) {
        return;
      }

      formik.setFieldValue('address', callLocation.address);
      formik.setFieldValue('latitude', callLocation.latitude);
      formik.setFieldValue('longitude', callLocation.longitude);
      if (!callLocation.address) {
        triggerReverseGeocode({
          latitude: callLocation.latitude,
          longitude: callLocation.longitude,
        });
      }
    },
    [callQueue, triggerReverseGeocode],
  );

  const handleCancel = (): void => {
    if (existingRequest) {
      dispatch(setIsEditing(false));
      return;
    }
    if (breadCrumbs.includes('list_requests') && breadCrumbs.length > 1) {
      dispatch(setResourcePanelView('list_requests'));
    } else {
      dispatch(closeResourcePanel());
    }
  };

  const hasBlockingErrors = (values: TowProFormValues): boolean =>
    Object.keys(validateTowProForm(values)).length > 0;

  const handleSubmit = async (values: TowProFormValues): Promise<void> => {
    const body = buildTowProDispatchRequest(values);
    if (!body) {
      return;
    }

    setSubmitError(null);
    try {
      if (existingRequest) {
        await updateTowProRequest({
          requestId: existingRequest.id,
          body,
        }).unwrap();
        dispatch(setIsEditing(false));
        return;
      }

      const created = await createTowProRequest(body).unwrap();
      dispatch(markTransportSeen(String(created.id)));
      dispatch(
        setSelectedRequest({
          requestId: String(created.id),
          detailView: 'towpro',
        }),
      );
    } catch {
      setSubmitError(SUBMIT_ERROR_MESSAGE);
    }
  };

  return (
    <div className={styles.form} data-name="towpro-form">
      <div className={styles.title}>
        <Text color="primaryText" size="large" fontWeight="bold">
          {existingRequest ? 'Edit Tow Request' : 'New Tow Request'}
        </Text>
      </div>

      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        validate={validateTowProForm}
        validateOnMount
        validateOnChange={false}
        validateOnBlur
        onSubmit={handleSubmit}
      >
        {formik => (
          <Form id="towProForm">
            <TowProFormSection title="Request">
              <SelectLocationBanner geoSelectionKey={TOW_PRO} />
              <TowProAddressField
                value={formik.values.address}
                error={
                  formik.touched.address && formik.errors.address
                    ? formik.errors.address
                    : undefined
                }
                geocodedResults={geocodedResults}
                isReverseGeocoding={isReverseGeocoding}
                onAddressChange={handleAddressChange}
                onSelectSuggestion={handleSelectSuggestion}
                onBlur={formik.handleBlur}
              />
              <SelectField
                formik={formik}
                name="region_id"
                label="Region*"
                options={regionOptions}
                placeholder="Select region"
                isLoading={isLoadingRegions}
              />
              <SelectField
                formik={formik}
                name="reason"
                label="Reason*"
                options={TOWPRO_REASON_OPTIONS}
                placeholder="Select reason"
              />
              <TextField
                formik={formik}
                name="related_call"
                label="Related Call"
                placeholder="(555) 555-5555"
                onValueChange={handleRelatedCallChange}
              />
            </TowProFormSection>

            <TowProFormSection title="Vehicle">
              <TextField
                formik={formik}
                name="vin"
                label="VIN"
                placeholder="Enter VIN"
                onValueChange={handleVinChange}
                transformValue={normalizeVin}
                warning={
                  formik.touched.vin
                    ? (getVinWarning(formik.values.vin) ?? undefined)
                    : undefined
                }
              />
              <div className={styles.row}>
                <TextField formik={formik} name="make" label="Make" />
                <TextField formik={formik} name="model" label="Model" />
              </div>
              <div className={styles.row}>
                <TextField formik={formik} name="vehicle_year" label="Year" />
                <TextField formik={formik} name="color" label="Color" />
              </div>
              <div className={styles.row}>
                <TextField
                  formik={formik}
                  name="license_plate_number"
                  label="License plate #"
                />
                <SelectField
                  formik={formik}
                  name="license_plate_state"
                  label="License plate state"
                  options={STATE_OPTIONS}
                  placeholder="State"
                />
              </div>
            </TowProFormSection>

            <TowProFormSection title="Incident & Officer">
              <TextField
                formik={formik}
                name="incident_number"
                label="Incident number"
              />
              <TextField formik={formik} name="agency_id" label="Agency ID" />
              <div className={styles.row}>
                <TextField
                  formik={formik}
                  name="officer_name"
                  label="Officer name"
                  placeholder="Full name"
                />
                <TextField
                  formik={formik}
                  name="badge_number"
                  label="Badge #"
                />
              </div>
            </TowProFormSection>

            <TowProFormSection title="Vehicle Owner">
              <div className={styles.row}>
                <TextField
                  formik={formik}
                  name="owner_first_name"
                  label="First name"
                />
                <TextField
                  formik={formik}
                  name="owner_last_name"
                  label="Last name"
                />
              </div>
              <TextField
                formik={formik}
                name="owner_phone"
                label="Phone"
                placeholder="(555) 555-5555"
              />
              <TextField
                formik={formik}
                name="owner_street"
                label="Street"
                placeholder="Street address"
              />
              <div className={styles.row}>
                <TextField formik={formik} name="owner_apt" label="Apt" />
                <TextField formik={formik} name="owner_city" label="City" />
              </div>
              <div className={styles.row}>
                <SelectField
                  formik={formik}
                  name="owner_state"
                  label="State"
                  options={STATE_OPTIONS}
                  placeholder="State"
                />
                <TextField formik={formik} name="owner_zip" label="Zip code" />
              </div>
            </TowProFormSection>

            <TowProFormSection title="Additional">
              <div className={styles.field}>
                <Label>
                  <Text size="small" color="primaryText">
                    Notes
                  </Text>
                </Label>
                <TextArea
                  id="notes"
                  name="notes"
                  placeholder="Optional context"
                  rows={3}
                  fontSize="14px"
                  value={formik.values.notes}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </TowProFormSection>

            {submitError && (
              <div className={styles.errorBanner} role="alert">
                <Text color="background" size="small">
                  {submitError}
                </Text>
              </div>
            )}
            <div className={styles.buttons}>
              <Button
                grow
                btnType="tertiary"
                type="button"
                onClick={handleCancel}
                disabled={isSubmitting}
                mx={0}
                dataName="towpro-form-cancel-button"
              >
                Cancel
              </Button>
              <Button
                grow
                type="submit"
                btnType="primary"
                disabled={isSubmitting || hasBlockingErrors(formik.values)}
                dataName="towpro-form-submit-button"
              >
                {isSubmitting
                  ? 'Submitting...'
                  : existingRequest
                    ? 'Resend / Update'
                    : 'Submit'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default TowProForm;
